import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "./Button";
import IconButton from "./IconButton";
import ConfirmDialog from "./ConfirmDialog";
import { cn } from "./cn";
import type { Action } from "./action";

type Props = {
  actions: Action[];
  /** Compact = small row-level density (row actions); default = md buttons for page headers. */
  density?: "compact" | "default";
  className?: string;
};

/**
 * Renders a row of Action buttons. Handles confirm-to-fire flow centrally so
 * individual pages no longer manage their own "delete modal" state.
 */
const ActionBar: React.FC<Props> = ({
  actions,
  density = "default",
  className,
}) => {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState<Action | null>(null);
  const visible = actions.filter((a) => !a.hidden);

  const trigger = (a: Action) => {
    if (a.disabled || a.loading) return;
    if (a.href) {
      navigate(a.href);
      return;
    }
    if (a.confirm) {
      setConfirming(a);
      return;
    }
    a.onClick?.();
  };

  return (
    <>
      <div className={cn("flex items-center gap-2", className)}>
        {visible.map((a) => {
          const variant = a.variant ?? "secondary";
          const size = density === "compact" ? "sm" : "md";

          if (a.iconOnly && a.icon) {
            return (
              <IconButton
                key={a.label}
                aria-label={a.label}
                onClick={() => trigger(a)}
                disabled={a.disabled || a.loading}
                variant={variant === "destructive" ? "ghost" : "secondary"}
                size={size}
                className={
                  variant === "destructive"
                    ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                    : undefined
                }
                title={a.label}
              >
                {a.icon}
              </IconButton>
            );
          }

          return (
            <Button
              key={a.label}
              variant={variant}
              size={size}
              onClick={() => trigger(a)}
              disabled={a.disabled || a.loading}
              aria-label={a.label}
            >
              {a.icon}
              {a.label}
            </Button>
          );
        })}
      </div>

      <ConfirmDialog
        open={Boolean(confirming)}
        title={confirming?.confirm?.title ?? ""}
        description={confirming?.confirm?.description}
        confirmLabel={confirming?.confirm?.confirmLabel ?? confirming?.label}
        cancelLabel={confirming?.confirm?.cancelLabel}
        destructive={confirming?.variant === "destructive"}
        onCancel={() => setConfirming(null)}
        onConfirm={async () => {
          const action = confirming;
          setConfirming(null);
          if (action) await action.onClick?.();
        }}
      />
    </>
  );
};

export default ActionBar;
